import { fetcher } from '../../../lib/api-fetcher';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';


export type TdsStatus = 'PENDING' | 'CHALLAN_RECORDED' | 'FILED';
export type GstStatus = 'PENDING' | 'FILED';

export interface TdsRegisterItem {
  id: string;
  deducteeName: string;
  deducteePan: string;
  sectionCode: string;
  paymentDate: string;
  amountPaid: number;
  tdsAmount: number;
  status: TdsStatus;
  challanBsrCode?: string;
  challanDate?: string;
  tdsChallanNo?: string;
  challanAckNo?: string;
}

export interface GstRegisterItem {
  id: string;
  customerName: string;
  customerGstin: string | null;
  invoiceNo: string;
  invoiceDate: string;
  taxableValue: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  totalTax: number;
  status: GstStatus;
  filedDate?: string;
  filedAckNo?: string;
}

export interface ListTdsResponse {
  data: TdsRegisterItem[];
  meta: {
    total: number;
    page: number;
    limit: number;
  };
}

export interface ListGstResponse {
  data: GstRegisterItem[];
  meta: {
    total: number;
    page: number;
    limit: number;
  };
}

export interface RecordChallanPayload {
  challanNo: string;
  challanDate: string;
}

export interface Export26qItem {
  deducteeCode: string;
  deducteePan: string;
  deducteeName: string;
  paymentDate: string;
  amountPaid: number;
  tdsAmount: number;
  rateAppliedPct: number;
}

export interface ExportGstr1Item {
  gstin: string;
  receiverName: string;
  invoiceNumber: string;
  invoiceDate: string;
  invoiceValue: number;
  placeOfSupply: string;
  reverseCharge: boolean;
  invoiceType: string;
  eCommerceGstin: string | null;
  rate: number;
  taxableValue: number;
  cessAmount: number;
}

export const taxKeys = {
  all: ['tax'] as const,
  tdsLists: () => [...taxKeys.all, 'tds'] as const,
  tdsList: (filters: object) => [...taxKeys.tdsLists(), filters] as const,
  gstLists: () => [...taxKeys.all, 'gst'] as const,
  gstList: (filters: object) => [...taxKeys.gstLists(), filters] as const,
};

export function useTdsRegister(filters: { schemeId?: string; page: number; limit: number }) {
  return useQuery<ListTdsResponse>({
    queryKey: taxKeys.tdsList(filters),
    queryFn: async () => {
      const qs = new URLSearchParams({ page: String(filters.page), limit: String(filters.limit) });
      if (filters.schemeId) qs.append('schemeId', filters.schemeId);
      return fetcher.get<ListTdsResponse>(`/staff/v1/tax/tds-register?${qs}`);
    },
  });
}

export function useGstRegister(filters: { schemeId?: string; page: number; limit: number }) {
  return useQuery<ListGstResponse>({
    queryKey: taxKeys.gstList(filters),
    queryFn: async () => {
      const qs = new URLSearchParams({ page: String(filters.page), limit: String(filters.limit) });
      if (filters.schemeId) qs.append('schemeId', filters.schemeId);
      return fetcher.get<ListGstResponse>(`/staff/v1/tax/gst-register?${qs}`);
    },
  });
}

export function useRecordChallan() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { id: string; body: RecordChallanPayload }>({
    mutationFn: async ({ id, body }) => {
      return fetcher.post<void>(`/staff/v1/tax/tds/${id}/challan`, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxKeys.tdsLists() });
    },
  });
}

export function useMarkTdsFiled() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      return fetcher.post<void>(`/staff/v1/tax/tds/${id}/mark-filed`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxKeys.tdsLists() });
    },
  });
}

export function useMarkGstFiled() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      return fetcher.post<void>(`/staff/v1/tax/gst/${id}/mark-filed`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxKeys.gstLists() });
    },
  });
}

export function useExport26Q() {
  return useMutation<Export26qItem[], Error, void>({
    mutationFn: async () => {
      return fetcher.get<Export26qItem[]>('/staff/v1/tax/exports/26q');
    },
  });
}

export function useExportGstr1() {
  return useMutation<ExportGstr1Item[], Error, void>({
    mutationFn: async () => {
      return fetcher.get<ExportGstr1Item[]>('/staff/v1/tax/exports/gstr1');
    },
  });
}
